import styles from './AbbrSidebarPopover.module.scss';
import { ABBR_POPOVER_MENU_DEFS } from 'components/SideNavbar/AbbrSidebarPopover/AbbrSidebarPopover.defs.tsx';
import Flex from '@lotlinx-aws/sparky-component-library/Sparky/Flex';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import classNames from 'classnames';
import { IconArrowLeft, IconChevronRight, IconPlus } from '@tabler/icons-react';
import Spacer from '@lotlinx-aws/sparky-component-library/Sparky/Spacer';
import Divider from '@lotlinx-aws/sparky-component-library/Sparky/Divider';
import { useAtom } from 'jotai';
import { PopoeverMenuKeys, sidebarPopoverAtom } from 'components/SideNavbar';
import { showReportGenerationAtom } from 'components/ReportsGenerationModal';
import { v4 as uuid } from 'uuid';
import Badge from '@lotlinx-aws/sparky-component-library/Sparky/Badge';
import {
  LinkKeepParams,
  useNavigateKeepParams,
} from 'components/KeepParamsRouter/KeepParamsRouter.tsx';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import CacheRefresh from 'components/SideNavbar/CacheRefresh';
import { useSelectedDealer } from '@lotlinx-aws/dealer-selector';
import useFeatureFlag from 'hooks/useFeatureFlag.tsx';
import { useLocation } from 'react-router';
import useAccess from 'hooks/useAccess';

interface AbbrSidebarPopoverProps {
  campaignNotificationNumber: number;
  /** Already capped for display (max 999); undefined hides the badge. */
  idpNotificationBadgeCount?: number;
  setIsPopoverOpen: (isOpen: boolean) => void;
}
/**
 * AbbrSidebarPopover
 */
export default function AbbrSidebarPopover({
  campaignNotificationNumber,
  idpNotificationBadgeCount,
  setIsPopoverOpen,
}: AbbrSidebarPopoverProps) {
  const [popoverMenu, setPopoverMenu] = useAtom(sidebarPopoverAtom);
  const [, setShowReportGeneration] = useAtom(showReportGenerationAtom);
  const location = useLocation();
  const { isVmbiSubscribedOnly, isLoading: isAccessLoading } = useAccess();

  const navigate = useNavigateKeepParams();

  const handleClick = (key: PopoeverMenuKeys | 'reports' | undefined, title: string) => {
    if (key && key !== 'reports' && key !== 'leadRevival') {
      setPopoverMenu(key);
    } else if (title === 'Shoppers') {
      navigate('leadRevival');
    } else if (title === 'Reports') {
      setShowReportGeneration(true);
      setIsPopoverOpen(false);
    }
  };

  if (!popoverMenu || popoverMenu === 'leadRevival' || popoverMenu === 'more') {
    return (
      <div className={styles.abbrSidebarPopover}>
        <Flex alignItems='center' padding='12px' margin='16px 16px 0'>
          <Text type='label-emphasized-01'>More</Text>
        </Flex>

        {Object.values(ABBR_POPOVER_MENU_DEFS).map((item, index) => {
          const isSelected = item.pathname?.some((p) => location.pathname.split('/').includes(p));

          if (
            (item.title === 'Campaigns' ||
              item.title === 'Dealer management' ||
              item.title === 'Reports' ||
              item.title === 'Shoppers') &&
            (isVmbiSubscribedOnly || isAccessLoading)
          ) {
            return <></>;
          }

          return (
            <div key={index}>
              {item.title === 'Reports' && <Divider style={{ margin: '6px 0' }} />}
              <div
                className={classNames([
                  styles.label,
                  index === Object.values(ABBR_POPOVER_MENU_DEFS).length - 1 && styles.lastLabel,
                ])}
                onClick={() => handleClick(item.key, item.title)}
              >
                <Flex height='16px' width='16px' alignItems='center' justifyContent='center'>
                  {item.icon}
                </Flex>
                <Text
                  type={isSelected ? 'label-01' : 'label-02'}
                  color={isSelected ? 'navy' : 'primary'}
                >
                  {item.title}
                </Text>
                {item.title === 'Shoppers' &&
                  idpNotificationBadgeCount !== undefined &&
                  idpNotificationBadgeCount !== 0 && (
                    <Badge
                      className={classNames([
                        styles.badgeContainer,
                        idpNotificationBadgeCount.toString().length > 2 && styles.large,
                      ])}
                    >
                      <Text as='div' lineHeight='1' size={12} fontWeight={5}>
                        {idpNotificationBadgeCount}
                      </Text>
                    </Badge>
                  )}
                <Spacer axis='horizontal' style={{ margin: 'auto' }} />
                {item.children && (
                  <Flex height='16px' width='16px' alignItems='center' justifyContent='center'>
                    <IconChevronRight color='var(--icon-subtle)' />
                  </Flex>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  } else {
    return (
      <SidebarPopoverContent
        activeKey={popoverMenu}
        closeMenu={() => {
          setIsPopoverOpen(false);
          setPopoverMenu(undefined);
        }}
        campaignNotificationNumber={campaignNotificationNumber}
        onReturnToMainMenu={() => {
          setPopoverMenu('more');
        }}
        usingDivider={true}
      />
    );
  }
}

interface SidebarPopoverContentProps {
  activeKey: Exclude<PopoeverMenuKeys, 'more' | 'leadRevival'>;
  closeMenu: () => void;
  campaignNotificationNumber: number;
  onReturnToMainMenu: () => void;
  usingDivider: boolean;
}

function SidebarPopoverContent({
  activeKey,
  closeMenu,
  onReturnToMainMenu,
  campaignNotificationNumber,
}: SidebarPopoverContentProps) {
  const dealer = useSelectedDealer();
  const navigate = useNavigateKeepParams();
  const location = useLocation();
  const { isVmbiSubscribedOnly, isLoading: isAccessLoading } = useAccess();

  const clickHandler = (location: string) => {
    navigate(location);
    closeMenu();
  };

  return (
    <Flex column width='100%' className={styles.abbrSidebarPopoverContent}>
      <Flex
        alignItems='center'
        padding='12px'
        margin='16px 16px 0'
        onClick={onReturnToMainMenu}
        columnGap='8px'
        width='calc(100% - 32px)'
        className={styles.returnToMainMenuBtn}
      >
        <Flex width='16px' height='16px' alignItems='center' justifyContent='center'>
          <IconArrowLeft color='var(--icon-strong)' />
        </Flex>
        <Text type='label-emphasized-01'>{ABBR_POPOVER_MENU_DEFS[activeKey].title}</Text>
      </Flex>
      {ABBR_POPOVER_MENU_DEFS[activeKey].children?.map((link, index) => {
        const showLink = useFeatureFlag(link.flag);
        const hideLink =
          (isVmbiSubscribedOnly || isAccessLoading) && link.isVmbiSubscriptionRestricted;

        if (link.flag !== undefined && !showLink) {
          return <></>;
        }

        if (hideLink) {
          return <></>;
        }

        if (link.title === 'divider') {
          return <Divider style={{ margin: '6px 0' }} />;
        } else {
          return (
            <Flex
              margin={
                (activeKey === 'analytics' || activeKey === 'inventory') &&
                ABBR_POPOVER_MENU_DEFS[activeKey].children &&
                ABBR_POPOVER_MENU_DEFS[activeKey].children.length - 1 === index
                  ? '0 16px 16px'
                  : '0 16px'
              }
              padding='12px'
              columnGap='8px'
              alignItems='center'
              width='calc(100% - 32px)'
              key={uuid()}
              onClick={() => clickHandler(link.location)}
              className={styles.link}
            >
              <Flex height='16px' width='16px' alignItems='center' justifyContent='center'>
                {link.icon}
              </Flex>
              <Text
                type={link.location === location.pathname ? 'label-01' : 'label-02'}
                color={link.location === location.pathname ? 'navy' : 'primary'}
              >
                {link.title}
              </Text>

              {link.title === 'Performance Results' && campaignNotificationNumber > 0 && (
                <Badge
                  className={classNames([
                    styles.badgeContainer,
                    campaignNotificationNumber.toString().length > 2 && styles.large,
                  ])}
                >
                  <Text as='div' lineHeight='1' size={12} fontWeight={5}>
                    {campaignNotificationNumber}
                  </Text>
                </Badge>
              )}
            </Flex>
          );
        }
      })}
      {activeKey === 'campaigns' && (
        <>
          <Divider style={{ margin: '6px 0' }} />
          <LinkKeepParams to={'/campaign_creation'} className={styles.createCampaignLink}>
            <Button
              variant='outline'
              leftIcon={<IconPlus size={20} />}
              disabled={location.pathname === '/campaign_creation'}
              className={styles.createCampaignBtn}
            >
              Create a campaign
            </Button>
          </LinkKeepParams>
        </>
      )}
      {activeKey === 'dealerManagement' && (
        <CacheRefresh padding='12px 0 16px' margin='0 16px' width='calc(100% - 32px)' />
      )}
    </Flex>
  );
}
